// client/src/pages/admin/Contactadmin.jsx

import React, { useState, useEffect, useContext } from 'react';
import { AnnouncementDetailModal } from '../../modals';
import { useNavigate } from 'react-router-dom';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import { GlobalContext } from '../../context/provider';
import useAnnouncement from '../../hooks/useAnnouncement';
import { ADMIN_LOGIN_ID } from '../../constants/roles';

import '../../styles/globalstyles.css';
import '../admin/adminMessaging.css';

const ContactAdmin = () => {
  const navigate = useNavigate();
  const { authState: { authData } } = useContext(GlobalContext);

  const { getStudentAnnouncements } = useAnnouncement();

  const [isLoading, setIsLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  const userId = authData?.loginId || authData?.id;

  useEffect(() => { loadAnnouncements(); }, [userId]);

  const loadAnnouncements = async () => {
    setIsLoading(true);
    try {
      if (!userId) return;
      const response = await getStudentAnnouncements(userId, 1, 100);
      if (response.success) {
        // Only show admin broadcasts
        const adminOnly = (response.announcements || []).filter(
          a => a.instructorId === ADMIN_LOGIN_ID || a.courseName === 'Admin Broadcast'
        );
        setAnnouncements(adminOnly);
      }
    } catch (error) {
      console.error('Failed to load announcements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityConfig = (priority) => ({
    urgent: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  label: '🚨 Urgent' },
    high:   { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: '🔴 High'   },
    medium: { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', label: '🔵 Medium' },
    low:    { color: '#10b981', bg: 'rgba(16,185,129,0.1)', label: '🟢 Low'    },
  }[priority] || { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', label: '🔵 Medium' });

  const unreadCount = announcements.filter(a => !a.read).length;

  const profileHeaderData = {
    ...authData,
    title: '📢 Admin Announcements',
  };

  return (
    <div className="main-container">
      <div className="main-content">

        {/* Header */}
        <div className="header-row">
          <ProfileHeader data={profileHeaderData} activePage="messages" />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={0} icons={['home', 'back']} />
        </div>

        <div className="am-page-body">

          {/* ── Stats Row ── */}
          <div className="am-stats-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="am-stat-card am-stat-sent">
              <div className="am-stat-icon-wrap"><span className="am-stat-icon">📨</span></div>
              <div className="am-stat-info">
                <div className="am-stat-number">{announcements.length}</div>
                <div className="am-stat-label">Total Announcements</div>
                <div className="am-stat-sub">From admin</div>
              </div>
            </div>
            <div className="am-stat-card am-stat-urgent">
              <div className="am-stat-icon-wrap"><span className="am-stat-icon">🔔</span></div>
              <div className="am-stat-info">
                <div className="am-stat-number">{unreadCount}</div>
                <div className="am-stat-label">Unread</div>
                <div className="am-stat-sub">Not yet opened</div>
              </div>
            </div>
            <div className="am-stat-card am-stat-recent">
              <div className="am-stat-icon-wrap"><span className="am-stat-icon">🕐</span></div>
              <div className="am-stat-info">
                <div className="am-stat-number">
                  {announcements.filter(a =>
                    (new Date() - new Date(a.createdAt)) < 7 * 24 * 60 * 60 * 1000
                  ).length}
                </div>
                <div className="am-stat-label">This Week</div>
                <div className="am-stat-sub">Last 7 days</div>
              </div>
            </div>
          </div>

          {/* ── Header ── */}
          <div className="am-history-header">
            <div className="am-history-title">
              <h3>📋 Announcements</h3>
              {unreadCount > 0 && (
                <span className="am-history-count">{unreadCount} unread</span>
              )}
            </div>
          </div>

          {/* ── Announcements List ── */}
          <div className="am-history-list">
            {isLoading ? (
              <div className="loading-container">
                <div className="loader"></div>
                <p>Loading announcements...</p>
              </div>
            ) : announcements.length > 0 ? (
              announcements.map((announcement, idx) => {
                const config = getPriorityConfig(announcement.priority);
                return (
                  <div
                    key={idx}
                    className="am-announcement-card"
                    onClick={() => setSelectedAnnouncement(announcement)}
                    style={{
                      '--priority-color': config.color,
                      '--priority-bg': config.bg,
                      opacity: announcement.read ? 0.85 : 1
                    }}
                  >
                    <div className="am-card-priority-bar" style={{ background: config.color }} />
                    <div className="am-card-body">
                      <div className="am-card-top">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                          <h4 className="am-card-title">{announcement.title}</h4>
                          {!announcement.read && (
                            <span style={{
                              background: '#ef4444',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: '700',
                              padding: '2px 7px',
                              borderRadius: '20px',
                              flexShrink: 0
                            }}>NEW</span>
                          )}
                        </div>
                        <span
                          className="am-card-priority-badge"
                          style={{ color: config.color, background: config.bg }}
                        >
                          {config.label}
                        </span>
                      </div>
                      <p className="am-card-preview">{announcement.body}</p>
                      <div className="am-card-footer">
                        <span className="am-card-meta">
                          👤 {announcement.instructorName || 'Admin'}
                        </span>
                        <span className="am-card-meta">
                          🕐 {new Date(announcement.createdAt).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="am-empty-state">
                <div className="am-empty-icon">📭</div>
                <h4>No announcements yet</h4>
                <p>Admin announcements will appear here</p>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Detail Modal */}
      <AnnouncementDetailModal
        visible={!!selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
        announcement={selectedAnnouncement}
      />

    </div>
  );
};

export default ContactAdmin;
